import React, { FC, useState, useEffect, useRef } from 'react';
import { InputGroup } from '@blueprintjs/core';
import { CurrencyInputProps } from './CurrencyInputProps';
import { NBSP } from '@/utils/format';
import {
  isNumber,
  cleanValue,
  fixedDecimalValue,
  formatValue,
  padTrimValue,
  CleanValueOptions,
} from './utils';

/**
 * Séparateurs du champ monétaire : la convention UNIQUE du produit
 * (« 1 234,56 » — espace insécable pour les milliers, virgule décimale),
 * la même que celle rendue par `formattedAmount` dans toutes les grilles.
 *
 * Ils étaient auparavant déduits de la locale de l'application / du navigateur
 * via `Intl.NumberFormat(locale).formatToParts()` : sur un poste anglais la
 * saisie attendait « 1,234.56 » alors que l'affichage montrait « 1 234,56 »,
 * et le séparateur exact (U+00A0 vs U+202F) variait avec la version d'ICU
 * embarquée. Un appelant peut toujours imposer les siens via les props
 * `decimalSeparator` / `groupSeparator`.
 */
const getLocaleSeparators = (): {
  decimalSeparator: string;
  groupSeparator: string;
} => ({ decimalSeparator: ',', groupSeparator: NBSP });

export const CurrencyInput: FC<CurrencyInputProps> = ({
  allowDecimals = true,
  allowNegativeValue = true,
  id,
  name,
  className,
  decimalsLimit,
  defaultValue,
  disabled = false,
  maxLength: userMaxLength,
  value: userValue,
  onChange,
  onBlurValue,
  fixedDecimalLength,
  placeholder,
  precision,
  prefix,
  step,
  decimalSeparator,
  groupSeparator,
  turnOffSeparators = false,
  turnOffAbbreviations = false,
  inputGroupProps,
  ...props
}: CurrencyInputProps) => {
  const localeDefaults = getLocaleSeparators();
  decimalSeparator = decimalSeparator ?? localeDefaults.decimalSeparator;
  groupSeparator = groupSeparator ?? localeDefaults.groupSeparator;

  // Outgoing values are normalized to '.'-decimal so consumers (parseFloat,
  // backend, formik) keep working regardless of the UI separator.
  const toCanonical = (s: string | undefined): string | undefined => {
    if (s === undefined || s === null) return s;
    if (decimalSeparator === '.') return s;
    return String(s).replace(decimalSeparator!, '.');
  };
  // Incoming controlled values are canonical ('.'-decimal); convert to the
  // UI separator before formatValue() splits the decimal portion.
  const fromCanonical = (s: string | undefined): string | undefined => {
    if (s === undefined || s === null) return s;
    if (decimalSeparator === '.') return s;
    return String(s).replace('.', decimalSeparator!);
  };
  if (decimalSeparator === groupSeparator) {
    throw new Error('decimalSeparator cannot be the same as groupSeparator');
  }

  if (isNumber(decimalSeparator)) {
    throw new Error('decimalSeparator cannot be a number');
  }

  if (isNumber(groupSeparator)) {
    throw new Error('groupSeparator cannot be a number');
  }

  const formatValueOptions = {
    decimalSeparator,
    groupSeparator,
    turnOffSeparators,
    prefix,
  };

  const cleanValueOptions: Partial<CleanValueOptions> = {
    decimalSeparator,
    groupSeparator,
    allowDecimals,
    decimalsLimit: decimalsLimit || fixedDecimalLength || 2,
    allowNegativeValue,
    turnOffAbbreviations,
    prefix,
  };

  // Permissive decimal entry: when the locale's decimal mark isn't `.` and `.`
  // is not the group separator either, accept a single `.` as a decimal. Users
  // — especially when typing exchange rates or technical numbers — habitually
  // type "10.4484"; without this, cleanValue() drops the dot as an invalid
  // character and "10.4484" silently becomes "104484".
  const normalizeDotAsDecimal = (
    raw: string | undefined,
  ): string | undefined => {
    if (raw == null) return raw;
    if (decimalSeparator === '.' || groupSeparator === '.') return raw;
    if (raw.indexOf(decimalSeparator!) !== -1) return raw;
    const dotMatches = raw.match(/\./g);
    if (!dotMatches || dotMatches.length !== 1) return raw;
    return raw.replace('.', decimalSeparator!);
  };

  const _defaultValue =
    defaultValue !== undefined
      ? formatValue({
          value: fromCanonical(String(defaultValue)),
          ...formatValueOptions,
        })
      : '';
  const [stateValue, setStateValue] = useState(_defaultValue);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onFocus = (): number => (stateValue ? stateValue.length : 0);

  const processChange = (
    value: string,
    selectionStart?: number | null,
  ): void => {
    value = normalizeDotAsDecimal(value) ?? value;
    const valueOnly = cleanValue({ value, ...cleanValueOptions });

    if (!valueOnly) {
      onChange && onChange(undefined, name);
      setStateValue('');
      return;
    }

    if (userMaxLength && valueOnly.replace(/-/g, '').length > userMaxLength) {
      return;
    }

    if (valueOnly === '-') {
      onChange && onChange(undefined, name);
      setStateValue(value);
      return;
    }

    const formattedValue = formatValue({
      value: valueOnly,
      ...formatValueOptions,
    });

    /* istanbul ignore next */
    if (selectionStart !== undefined && selectionStart !== null) {
      const cursor =
        selectionStart + (formattedValue.length - value.length) || 1;
      setCursor(cursor);
    }

    setStateValue(formattedValue);

    onChange && onChange(toCanonical(valueOnly), name);
  };

  const handleOnChange = ({
    target: { value, selectionStart },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    processChange(value, selectionStart);
  };

  const handleOnBlur = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    value = normalizeDotAsDecimal(value) ?? value;
    const valueOnly = cleanValue({ value, ...cleanValueOptions });

    if (valueOnly === '-' || !valueOnly) {
      onBlurValue && onBlurValue(undefined, name);
      setStateValue('');
      return;
    }

    const fixedDecimals = fixedDecimalValue(
      valueOnly,
      decimalSeparator!,
      fixedDecimalLength,
    );

    // Add padding or trim value to precision
    const newValue = padTrimValue(
      fixedDecimals,
      decimalSeparator,
      precision || fixedDecimalLength,
    );
    onChange && onChange(toCanonical(newValue), name);
    onBlurValue && onBlurValue(toCanonical(newValue), name);

    const formattedValue = formatValue({
      value: newValue,
      ...formatValueOptions,
    });
    setStateValue(formattedValue);
  };

  const handleOnKeyDown = ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
    if (step && (key === 'ArrowUp' || key === 'ArrowDown')) {
      const currentValue =
        Number(
          userValue !== undefined
            ? userValue
            : cleanValue({ value: stateValue, ...cleanValueOptions }),
        ) || 0;
      const newValue =
        key === 'ArrowUp'
          ? String(currentValue + Number(step))
          : String(currentValue - Number(step));

      processChange(newValue);
    }
  };

  /* istanbul ignore next */
  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.setSelectionRange(cursor, cursor);
    }
  }, [cursor, inputRef]);

  const formattedPropsValue =
    userValue !== undefined
      ? formatValue({
          value: fromCanonical(String(userValue)),
          ...formatValueOptions,
        })
      : undefined;

  const handleInputRef = (ref: HTMLInputElement | null) => {
    inputRef.current = ref;
    return null;
  };

  return (
    <InputGroup
      type="text"
      inputMode="decimal"
      id={id}
      name={name}
      className={className}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      onFocus={onFocus}
      onKeyDown={handleOnKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      value={
        formattedPropsValue !== undefined && stateValue !== '-'
          ? formattedPropsValue
          : stateValue
      }
      inputRef={handleInputRef}
      {...inputGroupProps}
      {...props}
    />
  );
};

export { CurrencyInput as MoneyInputGroup };
